import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material'
import * as Yup from 'yup'
import Env from '@/config/Env'
import { useTranslation } from 'react-i18next'

const fileTypeMap = {
  pdf: '.pdf',
  word: '.doc',
  excel: '.xlsx',
  exe: '.exe',
}

const fileIconConfig = {
  pdf: {
    url: Env.PDF_URL,
    width: 22,
    height: 28,
  },
  word: {
    url: Env.Word_URL,
    width: 28,
    height: 32,
  },
  excel: {
    url: Env.Excel_URL,
    width: 22,
    height: 28,
  },
  exe: {
    url: Env.EXE_URL,
    width: 26,
    height: 34,
  },
}

const AttachmentDialog = ({ isOpen, onClose, onGenerateFakeAttachment }) => {
  const { t } = useTranslation()
  const [format, setFormat] = useState('html') // html / rtf
  const [fileType, setFileType] = useState('pdf')
  const [fileName, setFileName] = useState('')
  const [subText, setSubText] = useState('')

  const [errors, setErrors] = useState({})

  const validation = Yup.object().shape({
    fileName: Yup.string().required(t('tinymce.dialogs.attachment.file_name_required')),
  })

  const handleInsert = () => {
    validation
      .validate({ fileName }, { abortEarly: false })
      .then(() => {
        const safeFileName = (fileName || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')

        const safeSubText = (subText || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')

        const ext = fileTypeMap[fileType] || ''
        const fullFileName = safeFileName.endsWith(ext) ? safeFileName : safeFileName + ext

        //  動態 icon
        const iconConfig = fileIconConfig[fileType] || fileIconConfig.pdf

        const subTextHtml = safeSubText
          ? `
<!-- sub text -->
<div style="font-size: 12px; color: #888; margin-top: 1px; line-height: 1.2;">
  ${safeSubText}
</div>
`
          : ''

        const subTextRTF = safeSubText
          ? `
<!-- sub text -->
<div style="font-size: 12px; color: #888; margin-top: 6px; text-align: center;">
  ${safeSubText}
</div>
`
          : ''

        // ===== HTML 模板 =====
        const htmlTemplate = `
<div style="font-family: 'Segoe UI', Arial, sans-serif; font-size: 14px; color: #323130; display: inline-block;">
  <table style="border: 1px solid #c8c8c8; border-radius: 4px; border-collapse: separate; border-spacing: 0;" cellspacing="0" cellpadding="0">
    <tbody>
      <tr>
        <!-- icon -->
        <td style="padding: 4px 2px 4px 6px; vertical-align: middle; width: 28px;">
          <a style="text-decoration: none;" href="{{fake_attachment_url}}">
            <img
              style="display:block;border:0;outline:none;text-decoration:none;"
              src="${iconConfig.url}"
              width="${iconConfig.width}"
              height="${iconConfig.height}"
            >
          </a>
        </td>

        <td style="padding: 4px 4px 4px 6px; vertical-align: middle;">
          <!-- file name -->
          <a style="text-decoration: none; color: #323130; font-size: 14px; display: block; line-height: 1.2;" href="{{fake_attachment_url}}">
            ${fullFileName}
          </a>

          ${subTextHtml}

        </td>

        <td style="width: 25px; text-align: center; vertical-align: middle;">
          <a style="font-size: 12px; color: #323130; text-decoration: none;" href="{{fake_attachment_url}}">⌵</a>
        </td>
      </tr>
    </tbody>
  </table>
</div>
<p>&nbsp;</p>
`

        // ===== RTF 模板 =====
        const rtfTemplate = `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" >
  <tr>
    <td align="center" style="padding: 10px;">

      <a style="text-decoration: none;" href="{{fake_attachment_url}}">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0">
          <!-- icon -->
          <tr>
            <td align="center">
              <a style="text-decoration: none;" href="{{fake_attachment_url}}">
                <img 
                  src="${iconConfig.url}"
                  alt="icon"
                  width="${iconConfig.width}"
                  height="${iconConfig.height}"
                  style="display:block; border:0; outline:none;">
              </a>
            </td>
          </tr>

          <!-- file name -->
          <tr>
            <td align="center" style="padding-top: 6px; font-size: 14px;">
              <a style="text-decoration: none; color: #323130; font-size: 14px; display: block; line-height: 1.2;" href="{{fake_attachment_url}}">
                ${fullFileName}
              </a>
            </td>
          </tr>
        </table>
      </a>
     
      ${subTextRTF}

    </td>
  </tr>
</table>
`

        //  根據 format 選擇
        const output = format === 'rtf' ? rtfTemplate : htmlTemplate

        onGenerateFakeAttachment(output)
        onClose()
      })
      .catch(error => {
        const newErrors = {}

        if (error?.inner?.length) {
          error.inner.forEach(err => {
            if (err.path) newErrors[err.path] = err.message
          })
        } else if (error?.path) {
          newErrors[error.path] = error.message
        }

        setErrors(newErrors)
      })
  }

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('tinymce.dialogs.attachment.title')}</DialogTitle>

      <DialogContent>
        {/* ===== FORMAT（ HTML / RTF ）===== */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 6, fontSize: 13 }}>
            {t('tinymce.dialogs.attachment.format_label')}
          </div>
          <RadioGroup row value={format} onChange={e => setFormat(e.target.value)}>
            <FormControlLabel value="html" control={<Radio />} label="HTML" />
            <FormControlLabel value="rtf" control={<Radio />} label="RTF" />
          </RadioGroup>
        </div>

        {/* ===== FILE TYPE ===== */}
        <FormControl fullWidth margin="normal">
          <InputLabel>{t('tinymce.dialogs.attachment.file_type_label')}</InputLabel>
          <Select
            value={fileType}
            label={t('tinymce.dialogs.attachment.file_type_label')}
            onChange={e => setFileType(e.target.value)}
          >
            <MenuItem value="pdf">PDF</MenuItem>
            <MenuItem value="word">Word</MenuItem>
            <MenuItem value="excel">Excel</MenuItem>
            <MenuItem value="exe">EXE</MenuItem>
          </Select>
        </FormControl>

        {/* ===== FILE NAME ===== */}
        <TextField
          label={t('tinymce.dialogs.attachment.file_name_label')}
          value={fileName}
          onChange={e => setFileName(e.target.value)}
          onFocus={() => setErrors(prev => ({ ...prev, fileName: '' }))} // 點擊時清除錯誤
          fullWidth
          margin="normal"
          error={Boolean(errors.fileName)}
          helperText={errors.fileName}
        />

        {/* ===== SUB TEXT ===== */}
        <TextField
          label={t('tinymce.dialogs.attachment.sub_text_label')}
          value={subText}
          onChange={e => setSubText(e.target.value)}
          fullWidth
          margin="normal"
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="secondary">
          {t('tinymce.dialogs.qrcode.close')}
        </Button>
        <Button variant="contained" onClick={handleInsert}>
          {t('tinymce.dialogs.attachment.insert')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AttachmentDialog
